import React, { useRef, useState, useEffect, useImperativeHandle, forwardRef, useCallback } from 'react';
import { Box } from '@mui/material';
import { QRCodeCanvas } from 'qrcode.react';
import {
  Shield,
  Group,
  Star,
  LocationOn,
  Build,
  Settings,
  Home,
  Palette,
  Favorite,
  Add
} from '@mui/icons-material';
import { getConfig } from '../configService';
import { saveFlyerPDF } from '../modules/pdfExport';
import { buildFlyerModel } from '../modules/printDocuments';
import { MUI_CATEGORY_ICONS, LEGACY_CATEGORY_ICONS, DEFAULT_CATEGORY_ICON_BY_KEY } from './categoryIconRegistry';
import PropTypes from 'prop-types';

// DPI constants for PDF capture
const CSS_DPI = 96;     // Standard CSS baseline (browser render at 96 DPI)
const DPI_SCALES = [600, 300, 150]; // Fallback scales in DPI: attempt 600 DPI first, then 300, then 150

/**
 * Compute the html2canvas scale factor for a given target DPI.
 * @param {number} targetDpi - Target DPI
 * @returns {number} Scale factor
 */
function computeScale(targetDpi) {
  return targetDpi / CSS_DPI;
}

/**
 * Check if a canvas is blank (all white or transparent pixels).
 * @param {HTMLCanvasElement} canvas - Canvas to check
 * @returns {boolean} True if canvas appears blank
 */
function isCanvasBlank(canvas) {
  if (!canvas) return true;

  try {
    const ctx = canvas.getContext('2d');
    if (!ctx) return true;

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    // Check if all pixels are white or transparent
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];

      if (a > 200 && (r < 250 || g < 250 || b < 250)) {
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error('Error checking canvas blank state:', error);
    return true;
  }
}

/**
 * Badge with circular background for business logo or initials.
 */
function LogoBadge({ provider }) {
  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .slice(0, 2)
      .map(w => w[0])
      .join('')
      .toUpperCase();
  };

  const hasLogo = provider?.thumbnailPhoto;

  return (
    <Box
      sx={{
        width: '0.8in',
        height: '0.8in',
        borderRadius: '50%',
        backgroundColor: '#e8f0fe',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        overflow: 'hidden',
        border: '2px solid #c5d9f2',
        backgroundImage: hasLogo ? `url(${provider.thumbnailPhoto})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
      data-testid="logo-badge"
    >
      {!hasLogo && (
        <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#1a365d' }}>
          {getInitials(provider?.name)}
        </span>
      )}
    </Box>
  );
}

/**
 * Decorative badge with icon and two-line caption.
 */
function DecorativeBadge({ icon: Icon, label1, label2 }) {
  return (
    <Box sx={{ textAlign: 'center', flex: 1 }}>
      <Box
        sx={{
          width: '0.6in',
          height: '0.6in',
          borderRadius: '50%',
          backgroundColor: '#e8f0fe',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 0.1in',
          border: '2px solid #c5d9f2'
        }}
      >
        <Icon sx={{ fontSize: '24px', color: '#1a365d' }} />
      </Box>
      <Box sx={{ fontSize: '9px', lineHeight: '1.2', color: '#1a365d', fontWeight: 'bold' }}>
        <div>{label1}</div>
        <div>{label2}</div>
      </Box>
    </Box>
  );
}

/**
 * Get the decorative icon by index for fallback when category icon is unavailable.
 */
function getDecorativeIcon(index) {
  const icons = [Build, Settings, Home, Palette, Favorite];
  return icons[index % icons.length];
}

/**
 * Resolve a service's icon: try category icon first, fall back to decorative cycle.
 * Returns { iconComponent, isLegacy } where isLegacy indicates if it's an SVG component.
 */
function resolveServiceIcon(service, index) {
  try {
    // Guard against null/undefined service
    if (!service || !service.category) {
      return { iconComponent: getDecorativeIcon(index), isLegacy: false };
    }

    // Try to resolve category icon
    const iconName = DEFAULT_CATEGORY_ICON_BY_KEY[service.category.toLowerCase()];
    if (!iconName) {
      return { iconComponent: getDecorativeIcon(index), isLegacy: false };
    }

    // Try legacy SVG icons first
    const LegacyIcon = LEGACY_CATEGORY_ICONS[iconName];
    if (LegacyIcon) {
      return { iconComponent: LegacyIcon, isLegacy: true };
    }

    // Try MUI icons
    const MuiIcon = MUI_CATEGORY_ICONS[iconName];
    if (MuiIcon) {
      return { iconComponent: MuiIcon, isLegacy: false };
    }

    // Fall back to decorative cycle if icon name didn't resolve to a real icon
    return { iconComponent: getDecorativeIcon(index), isLegacy: false };
  } catch (error) {
    console.error('Error resolving service icon:', error);
    return { iconComponent: getDecorativeIcon(index), isLegacy: false };
  }
}

/**
 * ProfileFlyer component: Displays a flyer for a business provider.
 * Letter portrait with header, photos, description, services, and QR code.
 *
 * Props:
 * - provider: The provider object
 * - publicProfileUrl: The full public profile URL
 * - onError: Optional callback when PDF save fails, receives error message
 */
const ProfileFlyer = forwardRef(function ProfileFlyer({ provider, publicProfileUrl, onError }, ref) {
  const flyerRef = useRef();
  const [isSavingPDF, setIsSavingPDF] = useState(false);

  // Load Google Fonts for script/handwriting face (must be before any conditional return)
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Caveat:wght@400..700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    return () => {
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    };
  }, []);

  const handlePrint = () => {
    window.print();
  };

  const handleSaveAsPDF = useCallback(async () => {
    setIsSavingPDF(true);

    try {
      if (!flyerRef.current) {
        throw new Error('Flyer reference not found.');
      }

      // Wait for fonts and images
      await new Promise(resolve => setTimeout(resolve, 200));

      // Temporarily disable any transform to ensure html2canvas captures at full resolution
      const originalTransform = flyerRef.current.style.transform;
      flyerRef.current.style.transform = 'none';

      try {
        // Capture the flyer with html2canvas, retrying at lower DPI if canvas hits browser limits
        const html2canvas = (await import('html2canvas')).default;
        let canvas = null;
        let successfulScale = null;

        for (const dpi of DPI_SCALES) {
          const scale = computeScale(dpi);
          console.log(`Attempting flyer capture at ${dpi} DPI (scale ${scale.toFixed(2)})`);

          try {
            canvas = await html2canvas(flyerRef.current, {
              scale,
              useCORS: true,
              backgroundColor: '#ffffff'
            });

            if (!canvas) {
              console.warn(`Canvas capture failed at ${dpi} DPI, retrying at lower scale`);
              continue;
            }

            // Verify canvas is not blank
            if (isCanvasBlank(canvas)) {
              console.warn(`Canvas appears blank at ${dpi} DPI, retrying at lower scale`);
              continue;
            }

            // Canvas is valid, use it
            successfulScale = dpi;
            console.log(`Successfully captured flyer at ${dpi} DPI`);
            break;
          } catch (captureError) {
            console.warn(`Error capturing at ${dpi} DPI: ${captureError.message}`);
            // Continue to next lower DPI
          }
        }

        if (!canvas || !successfulScale) {
          throw new Error('Failed to capture flyer at any resolution. Please try again.');
        }

        // Save PDF with the captured image
        const config = getConfig();
        await saveFlyerPDF(provider, publicProfileUrl, config, canvas);
      } finally {
        // Restore the original transform
        flyerRef.current.style.transform = originalTransform;
      }
    } catch (error) {
      console.error('Failed to save PDF:', error);
      const errorMessage = error.message || 'Failed to generate PDF. Please try again.';
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setIsSavingPDF(false);
    }
  }, [provider, publicProfileUrl, onError]);

  // Expose print and saveAsPDF methods via ref
  useImperativeHandle(ref, () => ({
    print: handlePrint,
    saveAsPDF: handleSaveAsPDF
  }), [handleSaveAsPDF]);

  if (!provider) {
    return <div>No provider data available</div>;
  }

  const config = getConfig();
  const flyerModel = buildFlyerModel(provider);

  // Count loaded images for reflow logic
  const numPhotos = Math.min(flyerModel.images.length, 5);
  const hasPhotos = numPhotos > 0;
  const heroPhoto = hasPhotos ? flyerModel.images[0] : null;
  const photoStrip = hasPhotos && flyerModel.images.length > 1
    ? flyerModel.images.slice(1, 5)
    : [];

  const categoryType = provider.categoryType || '';
  const hasTagline = Boolean(categoryType);

  return (
    <>
      <Box
        sx={{
          fontFamily: 'Arial, sans-serif',
          width: '8.5in',
          height: '11in',
          margin: '0 auto',
          backgroundColor: '#ffffff',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          '@media print': {
            width: '8.5in',
            height: '11in',
            margin: 0,
            backgroundColor: '#ffffff',
            overflow: 'hidden'
          }
        }}
        ref={flyerRef}
      >
      {/* ===== SECTION 1: HEADER ===== */}
      <Box
        sx={{
          backgroundColor: '#ffffff',
          padding: '0.3in 0.4in',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.3in',
          borderBottom: '1px solid #e8e8e8',
          flex: '0 0 auto',
          maxHeight: '1.3in',
          overflow: 'hidden',
          '@media print': {
            pageBreakInside: 'avoid'
          }
        }}
      >
        {/* Logo badge */}
        <LogoBadge provider={provider} />

        {/* Centre-left: Name and tagline */}
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <h1
            style={{
              margin: 0,
              fontSize: '28px',
              fontWeight: 'bold',
              color: '#1a365d',
              lineHeight: '1',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {flyerModel.name}
          </h1>
          {hasTagline && (
            <Box
              sx={{
                fontSize: '11px',
                letterSpacing: '2px',
                textTransform: 'uppercase',
                color: '#1a365d',
                fontWeight: '600',
                marginTop: '2px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {categoryType}
            </Box>
          )}
        </Box>

        {/* Right: Script text with underline */}
        <Box
          sx={{
            textAlign: 'right',
            whiteSpace: 'nowrap',
            paddingBottom: '4px',
            borderBottom: '3px solid #c5d9f2'
          }}
        >
          <Box
            sx={{
              fontFamily: 'Caveat, cursive',
              fontSize: '18px',
              fontWeight: '700',
              color: '#1a365d',
              lineHeight: '1'
            }}
          >
            Local People. Great Service.
          </Box>
        </Box>
      </Box>

      {/* ===== SECTION 2: UPPER BODY (with description, badges, hero photo) ===== */}
      {(flyerModel.description || hasPhotos) && (
        <Box
          sx={{
            backgroundColor: '#f8f9fa',
            padding: '0.3in 0.4in',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.2in',
            flex: '1 1 auto',
            minHeight: 0,
            overflow: 'hidden',
            '@media print': {
              pageBreakInside: 'avoid'
            }
          }}
        >
          {/* Row with left and right columns */}
          <Box sx={{ display: 'flex', gap: '0.2in', minHeight: 0 }}>
            {/* Left column: Description and badges */}
            <Box sx={{ width: '40%', display: 'flex', flexDirection: 'column', gap: '0.15in', minHeight: 0 }}>
              {/* Description */}
              {flyerModel.description && (
                <Box
                  sx={{
                    fontSize: '9px',
                    lineHeight: '1.4',
                    color: '#333',
                    textAlign: 'justify',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 4,
                    flex: '0 1 auto'
                  }}
                >
                  {flyerModel.description}
                </Box>
              )}

              {/* Three decorative badges */}
              <Box sx={{ display: 'flex', gap: '0.1in', marginTop: flyerModel.description ? 'auto' : '0', marginBottom: '0.5in' }}>
                <DecorativeBadge icon={Shield} label1="TRUSTED &" label2="RELIABLE" />
                <DecorativeBadge icon={Group} label1="LOCAL" label2="BUSINESS" />
                <DecorativeBadge icon={Star} label1="QUALITY" label2="SERVICE" />
              </Box>
            </Box>

            {/* Right column: Hero photo */}
            {hasPhotos && (
              <Box sx={{ width: '60%', position: 'relative', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                {/* Hero photo - 1.618:1 landscape (golden ratio) */}
                <Box
                  sx={{
                    flex: '0 0 auto',
                    overflow: 'hidden',
                    borderRadius: '4px',
                    backgroundColor: '#e8e8e8',
                    position: 'relative',
                    backgroundImage: `url(${config.imageBaseUrl}${provider.id}/${heroPhoto}-large.jpg)`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    aspectRatio: '1.618',
                    height: '2.78in'
                  }}
                  data-testid="hero-photo"
                />
              </Box>
            )}
          </Box>

          {/* Full-width wave SVG below the two-column row */}
          {hasPhotos && (
            <svg
              height="0.8in"
              viewBox="0 0 1000 100"
              style={{
                display: 'block',
                flex: '0 0 auto',
                marginLeft: '-0.4in',
                marginRight: '-0.4in',
                width: 'calc(100% + 0.8in)'
              }}
              preserveAspectRatio="none"
            >
              <path
                d="M 0 50 C 120 18 280 10 420 34 C 560 58 650 84 770 80 C 870 76 945 60 1000 50 L 1000 100 L 0 100 Z"
                fill="#8fb8dd"
              />
            </svg>
          )}
        </Box>
      )}

      {/* ===== SECTION 3: PHOTO STRIP ===== */}
      {photoStrip.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            gap: 0,
            height: '1.31in',
            flex: '0 0 1.31in',
            overflow: 'hidden',
            '@media print': {
              pageBreakInside: 'avoid'
            }
          }}
        >
          {photoStrip.map((imageId, index) => (
            <Box
              key={index}
              sx={{
                flex: 1,
                overflow: 'hidden',
                backgroundColor: '#e8e8e8',
                backgroundImage: `url(${config.imageBaseUrl}${provider.id}/${imageId}-large.jpg)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}
              data-testid="photo-strip-item"
            />
          ))}
        </Box>
      )}

      {/* ===== SECTION 4: LOWER BODY (Services and QR) ===== */}
      <Box
        sx={{
          backgroundColor: '#ffffff',
          padding: '0.3in 0.4in',
          display: 'flex',
          gap: '0.2in',
          borderTop: '1px solid #e8e8e8',
          flex: '0 0 auto',
          maxHeight: '3.2in',
          overflow: 'hidden',
          '@media print': {
            pageBreakInside: 'avoid'
          }
        }}
      >
        {/* Left: Services */}
        <Box sx={{ width: '50%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {flyerModel.displayedServices.length > 0 && (
            <>
              <h4
                style={{
                  margin: '0 0 0.1in 0',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  color: '#1a365d',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  flex: '0 0 auto'
                }}
              >
                OUR SERVICES
              </h4>

              {/* Service rows */}
              {flyerModel.displayedServices.map((service, index) => {
                const { iconComponent: IconComponent, isLegacy } = resolveServiceIcon(service, index);
                return (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.08in',
                      marginBottom: '4px',
                      fontSize: '9px',
                      color: '#333',
                      minHeight: 0,
                      overflow: 'hidden'
                    }}
                  >
                    {isLegacy ? (
                      <IconComponent width={16} height={16} fill="#1a365d" stroke="#1a365d" style={{ flexShrink: 0 }} />
                    ) : (
                      <IconComponent sx={{ fontSize: '16px', color: '#1a365d', flexShrink: 0 }} />
                    )}
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{service.name}</span>
                  </Box>
                );
              })}

              {/* "And More!" row if needed */}
              {flyerModel.hasMore && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.08in',
                    marginTop: '6px',
                    fontSize: '9px',
                    color: '#1a365d',
                    fontWeight: 'bold'
                  }}
                >
                  <Add sx={{ fontSize: '16px', color: '#1a365d', flexShrink: 0 }} />
                  <span>And More!</span>
                </Box>
              )}
            </>
          )}
        </Box>

        {/* Right: QR Code card with arrow and text */}
        <Box sx={{ width: '50%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {/* QR Code in rounded card */}
          <Box
            sx={{
              backgroundColor: '#ffffff',
              border: '1px solid #d0d0d0',
              borderRadius: '6px',
              padding: '0.1in',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              marginBottom: '0.1in'
            }}
          >
            {publicProfileUrl && (
              <QRCodeCanvas
                value={publicProfileUrl}
                level="H"
                size={120}
                marginSize={4}
                imageSettings={{
                  src: require('../assets/svgs/PairSelfCareIcon.png'),
                  height: 24,
                  width: 24,
                  excavate: true
                }}
              />
            )}
          </Box>

          {/* Script text with hand-drawn arrow */}
          <Box
            sx={{
              fontFamily: 'Caveat, cursive',
              fontSize: '14px',
              fontWeight: '700',
              color: '#1a365d',
              marginBottom: '0.08in',
              position: 'relative'
            }}
          >
            Scan to Learn More
            {/* Simple curved arrow SVG inline */}
            <svg
              width="30"
              height="20"
              style={{
                position: 'absolute',
                left: '-28px',
                top: '-4px'
              }}
              viewBox="0 0 30 20"
            >
              <path
                d="M 25 2 Q 15 8 10 15"
                stroke="#1a365d"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
              />
              <polygon points="10,15 8,10 12,12" fill="#1a365d" />
            </svg>
          </Box>

          {/* Bottom text */}
          <Box
            sx={{
              fontSize: '7px',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              color: '#1a365d',
              fontWeight: '600',
              textAlign: 'center',
              lineHeight: '1.3'
            }}
          >
            View Our Full Profile,<br />Photos, Reviews and More!
          </Box>
        </Box>
      </Box>

      {/* ===== SECTION 5: FOOTER ===== */}
      <Box
        sx={{
          backgroundColor: '#1a365d',
          color: '#ffffff',
          padding: '0.2in 0.4in',
          position: 'relative',
          overflow: 'hidden',
          flex: '0 0 auto',
          minHeight: '0.65in',
          marginTop: 'auto',
          '@media print': {
            pageBreakInside: 'avoid'
          }
        }}
      >
        {/* Wave SVG at top */}
        <svg
          width="100%"
          height="20"
          viewBox="0 0 800 20"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            display: 'block'
          }}
          preserveAspectRatio="none"
        >
          <path
            d="M0,10 Q50,0 100,10 T200,10 T300,10 T400,10 T500,10 T600,10 T700,10 T800,10 L800,20 L0,20 Z"
            fill="#1a365d"
          />
        </svg>

        {/* Faint mountains/trees silhouette inside */}
        <svg
          width="100%"
          height="20"
          viewBox="0 0 400 40"
          style={{
            position: 'absolute',
            top: '2px',
            left: 0,
            opacity: 0.15,
            display: 'block'
          }}
          preserveAspectRatio="none"
        >
          <polygon points="0,30 40,10 80,25 120,8 160,22 200,5 240,20 280,10 320,25 360,12 400,28 400,40 0,40" fill="#ffffff" />
          {/* Simple trees */}
          <circle cx="50" cy="28" r="2" fill="#ffffff" opacity="0.6" />
          <circle cx="150" cy="22" r="2" fill="#ffffff" opacity="0.6" />
          <circle cx="250" cy="20" r="2" fill="#ffffff" opacity="0.6" />
          <circle cx="350" cy="24" r="2" fill="#ffffff" opacity="0.6" />
        </svg>

        {/* Content */}
        <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'space-between', paddingTop: '0.15in' }}>
          {/* Left: Location + slogans */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.1in' }}>
            <LocationOn sx={{ fontSize: '18px' }} />
            <Box>
              <Box
                sx={{
                  fontSize: '10px',
                  fontWeight: 'bold',
                  letterSpacing: '1px',
                  textTransform: 'uppercase'
                }}
              >
                SERVING OUR COMMUNITY
              </Box>
              <Box
                sx={{
                  fontSize: '8px',
                  fontStyle: 'italic',
                  lineHeight: '1'
                }}
              >
                Quality Work. Stronger Communities.
              </Box>
            </Box>
          </Box>

          {/* Right: Support Local */}
          <Box sx={{ textAlign: 'right' }}>
            <Box
              sx={{
                fontSize: '8px',
                letterSpacing: '1px',
                textTransform: 'uppercase',
                fontWeight: '600'
              }}
            >
              SUPPORT LOCAL
            </Box>
            <Box
              sx={{
                fontSize: '8px',
                letterSpacing: '1px',
                textTransform: 'uppercase',
                fontWeight: '600'
              }}
            >
              SHOP LOCAL
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
    </>
  );
});

ProfileFlyer.propTypes = {
  provider: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    friendlyUrl: PropTypes.string,
    businessSummary: PropTypes.string,
    categoryType: PropTypes.string,
    thumbnailPhoto: PropTypes.string,
    address: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    postalCode: PropTypes.string,
    servicesProvided: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string
      })
    ),
    imageList: PropTypes.arrayOf(PropTypes.string)
  }),
  publicProfileUrl: PropTypes.string.isRequired,
  onError: PropTypes.func
};

export default ProfileFlyer;
